import { useMemo, useState } from 'react';
import { useAppState } from '../../context/LoadTestContext';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Checkbox, ListItemText, MenuItem, Skeleton, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useUnits } from '../units';
import { smoothRows } from '../smoothing';

// "All Sensors vs Time": load, average settlement and every active strain gauge
// as strips on one elapsed-time axis (pick which with the Graphs dropdown),
// load steps shaded alternately. Built
// from lvdt_graph (time, step, load, deflection) and timeseries (microstrain),
// which come from the same rows in the same order.
const SG_COLORS = {
  light: ['#0C447C', '#185FA5', '#2A73BF', '#378ADD', '#5A9FE5', '#7DB4EC', '#9FC8F1', '#BCD9F5',
    '#0C447C', '#185FA5', '#2A73BF', '#378ADD', '#5A9FE5', '#7DB4EC', '#9FC8F1', '#BCD9F5'],
  dark: ['#BAE6FD', '#93D5FB', '#6CC4F8', '#45B3F4', '#2A9FE3', '#1E88C7', '#1B71A6', '#185A85',
    '#BAE6FD', '#93D5FB', '#6CC4F8', '#45B3F4', '#2A9FE3', '#1E88C7', '#1B71A6', '#185A85'],
};
const SG_KEY = /^SG_microstrain\((\d+)\)$/;

function hoursLabel(min) {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

function Strip({ title, data, dataKeys, colors, bands, theme, format, unitLabel, showXAxis }) {
  return (
    <Box sx={{ flex: 1, minHeight: 90, display: 'flex', flexDirection: 'column' }}>
      <Typography variant='caption' color='text.secondary' sx={{ pl: 1 }}>{title} ({unitLabel})</Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart data={data} syncId='lt-all-sensors' margin={{ top: 2, right: 12, left: 0, bottom: showXAxis ? 0 : 2 }}>
            <CartesianGrid strokeDasharray='3 3' />
            {/* {bands.map((b) => (
              <ReferenceArea key={b.step} x1={b.t0} x2={b.t1} fill={theme.palette.primary.main} fillOpacity={0.07} ifOverflow='hidden' />
            ))} */}
            {bands.map((b) => (
              <ReferenceArea
                key={`${b.step}-${b.t0}`}
                x1={b.t0}
                x2={b.t1}
                // fill={b.step % 2 === 1 ? '#3b82f6' : '#0c4bb1'}
                fill='#3b82f6'
                fillOpacity={theme.palette.mode === 'dark' ? 0.22 : 0.16}
                stroke='none'
                ifOverflow='hidden'
              />
            ))}
            <XAxis dataKey='t' type='number' domain={['dataMin', 'dataMax']} tickFormatter={hoursLabel} fontSize={10} hide={!showXAxis} />
            <YAxis fontSize={10} width={48} tickFormatter={format} domain={['auto', 'auto']} />
            <Tooltip
              contentStyle={{ backgroundColor: theme.chart?.tooltipBg, color: theme.palette.text.secondary, fontSize: 12 }}
              labelFormatter={(t) => `${hoursLabel(t)} elapsed`}
              formatter={(v, name) => [v === null || v === undefined ? '—' : format(v), name]}
            />
            {dataKeys.map((k, i) => (
              <Line key={k.key} dataKey={k.key} name={k.name} stroke={colors[i % colors.length]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

// The strips, in the order they stack. The picker above the graph chooses
// which show; the ones shown share the graph's height equally.
const STRIPS = [
  { key: 'load', label: 'Load' },
  { key: 'settlement', label: 'Avg settlement' },
  { key: 'strain', label: 'Strain gauges' },
];
const STRIPS_KEY = 'lt-all-sensors-strips';

function readStrips() {
  try {
    const saved = JSON.parse(localStorage.getItem(STRIPS_KEY));
    const keys = Array.isArray(saved) ? STRIPS.map((s) => s.key).filter((k) => saved.includes(k)) : [];
    return keys.length ? keys : STRIPS.map((s) => s.key);
  } catch {
    return STRIPS.map((s) => s.key);
  }
}

function AllSensorsGraph() {
  const { loading, lvdtGraphData, loadTestData, stepSummaryData, allStepSummaryData, showUnloadSteps, smoothing, measurementUnits } = useAppState();
  const theme = useTheme();
  const units = useUnits();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';

  const strainUnitLabel = Object.values(measurementUnits?.sg ?? []).some((unit) => unit === 'digits') ? 'digits' : 'microstrain';
  const [strips, setStrips] = useState(readStrips);
  const chooseStrips = (keys) => {
    if (!keys.length) return; // keep at least one
    const ordered = STRIPS.map((s) => s.key).filter((k) => keys.includes(k));
    setStrips(ordered);
    try { localStorage.setItem(STRIPS_KEY, JSON.stringify(ordered)); } catch { /* not remembered */ }
  };

  // Unload steps (by their number in step_summary), cut from the time axis
  // while they are hidden, as on the LVDT graph: it ends with the last load
  // step, and an unload step mid-test leaves a gap.
  const hiddenSteps = useMemo(() => new Set(showUnloadSteps ? [] : (allStepSummaryData || [])
    .filter((s) => s.kind === 'U').map((s) => Number(s.step))), [allStepSummaryData, showUnloadSteps]);

  const { points, sgKeys, bands } = useMemo(() => {
    const rows = Array.isArray(lvdtGraphData) ? lvdtGraphData : [];
    const series = Array.isArray(loadTestData) ? loadTestData : [];
    const keys = series.length
      ? Object.keys(series[0]).filter((k) => SG_KEY.test(k)).sort((a, b) => Number(a.match(SG_KEY)[1]) - Number(b.match(SG_KEY)[1]))
      : [];
    const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
    // A reading a sensor missed carries its last value forward, so its line
    // stays unbroken; only a hidden stretch (a blank point) breaks the lines.
    const pts = [];
    let gap = false;
    let prev = null;
    rows.forEach((r, i) => {
      if (hiddenSteps.has(Number(r.step))) {
        gap = true;
        prev = null;
        return;
      }
      const t = Number(r.elapsed_min);
      if (gap && pts.length) {
        const blank = { t, load: null, deflection: null };
        keys.forEach((k) => { blank[k] = null; });
        pts.push(blank);
      }
      gap = false;
      const p = {
        t,
        step: r.step,
        load: num(r.load) === null ? null : units.toForce(num(r.load)),
        deflection: num(r.deflection) === null ? null : units.toLength(num(r.deflection)),
      };
      keys.forEach((k) => { p[k] = num(series[i]?.[k]); });
      if (prev) {
        ['load', 'deflection', ...keys].forEach((k) => { if (p[k] === null) p[k] = prev[k]; });
      }
      prev = p;
      pts.push(p);
    });
    const shown = new Set(stepSummaryData.map((s) => s.step));
    const ranges = [];
    let active = null;

    rows.forEach((r) => {
      const step = Number(r.step);
      const t = Number(r.elapsed_min);
      const load = num(r.load);

      if (
        !Number.isFinite(step)
        || !(step > 0)
        || !shown.has(step)
        || t === null
        || !Number.isFinite(t)
        || load === null
        || !Number.isFinite(load)
      ) {
        active = null;
        return;
      }

      if (active && active.step === step) {
        active.t1 = t;
      } else {
        active = { step, t0: t, t1: t };
        ranges.push(active);
      }
    });
    return {
      points: smoothRows(pts, ['load', 'deflection', ...keys], smoothing),
      sgKeys: keys,
      // bands: ranges.filter((_, i) => i % 2 === 0),
      bands: ranges
    };
  }, [lvdtGraphData, loadTestData, stepSummaryData, hiddenSteps, smoothing, units.system]);

  if (loading) return <Skeleton variant='rectangular' animation='wave' sx={{ borderRadius: 1, height: '100%', minHeight: 240 }} />;
  if (!points.length) {
    return <Typography color='text.secondary' sx={{ p: 2 }}>No readings to plot yet.</Typography>;
  }

  const stripProps = {
    load: {
      title: 'Load', unitLabel: units.forceUnit,
      dataKeys: [{ key: 'load', name: 'Load' }], colors: [theme.palette.mode === 'dark' ? '#38BDF8' : '#003A70'],
      format: (v) => Number(v).toFixed(0),
    },
    settlement: {
      title: 'Avg settlement', unitLabel: units.lengthUnit,
      dataKeys: [{ key: 'deflection', name: 'Avg settlement' }], colors: [theme.palette.mode === 'dark' ? '#7DD3FC' : '#0E7FB8'],
      format: (v) => Number(v).toFixed(units.lengthDecimals),
    },
    strain: {
      title: 'Strain gauges', unitLabel: strainUnitLabel,
      dataKeys: sgKeys.map((k) => ({ key: k, name: `SG ${k.match(SG_KEY)[1]}` })), colors: SG_COLORS[mode],
      format: (v) => Number(v).toFixed(0),
    },
  };

  return (
    <Box sx={{ height: '100%', minHeight: 300, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          select
          size='small'
          label='Graphs'
          value={strips}
          onChange={(e) => chooseStrips(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (keys) => STRIPS.filter((s) => keys.includes(s.key)).map((s) => s.label).join(', '),
          }}
          sx={{ minWidth: 220 }}
        >
          {STRIPS.map((s) => (
            <MenuItem key={s.key} value={s.key} dense>
              <Checkbox size='small' checked={strips.includes(s.key)} sx={{ p: 0.5, mr: 1 }} />
              <ListItemText primary={s.label} />
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {strips.map((key, i) => (
        <Strip key={key} {...stripProps[key]} data={points} theme={theme} bands={bands} showXAxis={i === strips.length - 1} />
      ))}
    </Box>
  );
}

export default AllSensorsGraph;
